import React, { useEffect, useState } from "react";
import {
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";
import {
  getAuth,
  onAuthStateChanged,
  signOut,
} from "firebase/auth";
import app from "../firebase.js";

function FinanceProtectedRoute({ children }) {
  const location = useLocation();
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [logoutLoading, setLogoutLoading] = useState(false);

  useEffect(() => {
    const auth = getAuth(app);

    const unsubscribe = onAuthStateChanged(
      auth,
      async (currentUser) => {
        if (!currentUser) {
          setUser(null);
          setLoading(false);
          return;
        }

        try {
          await currentUser.reload();
          setUser(currentUser);
        } catch (error) {
          console.error(
            "Erreur lors de la vérification du compte Finance :",
            error
          );
          setUser(currentUser);
        } finally {
          setLoading(false);
        }
      }
    );

    return () => unsubscribe();
  }, []);

  const handleLogout = async () => {
    const confirmation = window.confirm(
      "Voulez-vous vraiment vous déconnecter de votre compte Groupe HDC ?"
    );

    if (!confirmation) {
      return;
    }

    setLogoutLoading(true);

    try {
      await signOut(getAuth(app));
      navigate("/consulting/finance", { replace: true });
    } catch (error) {
      console.error("Erreur lors de la déconnexion :", error);
      window.alert(
        "La déconnexion a échoué. Veuillez réessayer."
      );
    } finally {
      setLogoutLoading(false);
    }
  };

  const returnTo = `${location.pathname}${location.search}`;

  if (loading) {
    return (
      <div style={styles.loadingPage}>
        <div style={styles.loader} />
        <p style={styles.loadingText}>
          Vérification de votre accès sécurisé…
        </p>
      </div>
    );
  }

  if (!user) {
    return (
      <Navigate
        to="/consulting/finance/connexion"
        replace
        state={{ returnTo }}
      />
    );
  }

  if (!user.emailVerified) {
    return (
      <Navigate
        to="/consulting/finance/connexion"
        replace
        state={{
          returnTo,
          verificationRequired: true,
        }}
      />
    );
  }

  const displayAccountBar =
    location.pathname !== "/consulting/finance/admin";

  return (
    <>
      {displayAccountBar && (
        <div style={styles.accountBar}>
          <div style={styles.accountIdentity}>
            <div style={styles.accountIcon}>
              {user.email?.charAt(0).toUpperCase() || "U"}
            </div>

            <div style={styles.accountInformation}>
              <span style={styles.accountLabel}>
                Compte connecté
              </span>
              <strong style={styles.accountEmail}>
                {user.email}
              </strong>
            </div>
          </div>

          <button
            type="button"
            style={{
              ...styles.logoutButton,
              ...(logoutLoading ? styles.disabledButton : {}),
            }}
            disabled={logoutLoading}
            onClick={handleLogout}
          >
            {logoutLoading ? "Déconnexion…" : "Se déconnecter"}
          </button>
        </div>
      )}

      {children}
    </>
  );
}

const styles = {
  loadingPage: {
    minHeight: "70vh",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    gap: "16px",
    background: "linear-gradient(145deg, #050816, #111936)",
    color: "#ffffff",
    fontFamily:
      "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },

  loader: {
    width: "42px",
    height: "42px",
    borderRadius: "50%",
    border: "4px solid rgba(255,255,255,0.18)",
    borderTopColor: "#7c3aed",
  },

  loadingText: {
    margin: 0,
    color: "#cbd5e1",
    fontSize: "13px",
    fontWeight: "700",
  },

  accountBar: {
    position: "fixed",
    top: "16px",
    right: "18px",
    zIndex: 9999,
    maxWidth: "calc(100vw - 36px)",
    padding: "9px 10px 9px 12px",
    display: "flex",
    alignItems: "center",
    gap: "12px",
    border: "1px solid rgba(255, 255, 255, 0.16)",
    borderRadius: "15px",
    background: "rgba(7, 11, 30, 0.94)",
    boxShadow: "0 12px 35px rgba(0, 0, 0, 0.28)",
    backdropFilter: "blur(14px)",
    color: "#ffffff",
    fontFamily:
      "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },

  accountIdentity: {
    minWidth: 0,
    display: "flex",
    alignItems: "center",
    gap: "9px",
  },

  accountIcon: {
    width: "32px",
    height: "32px",
    flexShrink: 0,
    display: "grid",
    placeItems: "center",
    borderRadius: "10px",
    background: "linear-gradient(135deg, #7c3aed, #2563eb)",
    color: "#ffffff",
    fontSize: "12px",
    fontWeight: "900",
  },

  accountInformation: {
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    gap: "2px",
  },

  accountLabel: {
    color: "#94a3b8",
    fontSize: "8px",
    fontWeight: "800",
    textTransform: "uppercase",
    letterSpacing: "0.06em",
  },

  accountEmail: {
    maxWidth: "180px",
    overflow: "hidden",
    color: "#ffffff",
    fontSize: "10px",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },

  logoutButton: {
    minHeight: "32px",
    padding: "0 11px",
    flexShrink: 0,
    border: "1px solid rgba(248, 113, 113, 0.32)",
    borderRadius: "9px",
    background: "rgba(239, 68, 68, 0.12)",
    color: "#fca5a5",
    fontSize: "9px",
    fontWeight: "900",
    cursor: "pointer",
  },

  disabledButton: {
    opacity: 0.6,
    cursor: "not-allowed",
  },
};

export default FinanceProtectedRoute;